import { apiClients, API_CONFIG } from '../config/apiClient';

export const extractionAPI = {
  /**
   * Iniciar una sesión de extracción por lotes para preservar resultados parciales
   * @param {number} documentTypeId - ID del tipo de documento
   * @param {number} totalFiles - Número total de archivos a procesar
   * @param {string} extractionMethod - Método de extracción
   * @returns {Promise} Información de la sesión creada
   */
  async startBatchSession(documentTypeId, totalFiles, extractionMethod = 'ocr_text') {
    try {
      const formData = new FormData();
      formData.append('document_type_id', documentTypeId);
      formData.append('total_files', totalFiles);
      formData.append('extraction_method', extractionMethod);
      
      const response = await apiClients.extraction.post('/batch/start', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Error al iniciar sesión'
      };
    }
  },

  /**
   * Finalizar una sesión de extracción por lotes
   * @param {number} sessionId - ID de la sesión
   * @param {string} finalStatus - Estado final (completada, interrumpida, error)
   * @returns {Promise} Resumen de la sesión finalizada
   */
  async finalizeBatchSession(sessionId, finalStatus = 'completada') {
    try {
      const formData = new FormData();
      formData.append('final_status', finalStatus);
      
      const response = await apiClients.extraction.post(`/batch/${sessionId}/finalize`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        }
      });
      
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Error al finalizar sesión'
      };
    }
  },
  
  // Cliente para compatibilidad con código existente
  get client() {
    return apiClients.extraction;
  },

  /**
   * Extraer datos de archivos usando IA
   * @param {FormData} formData - Datos del formulario con archivos y configuración
   * @param {function} onProgress - Callback para mostrar progreso
   * @param {object} options - Opciones adicionales (isSingle, timeout, sessionId)
   * @returns {Promise} Resultado de la extracción
   */
  async extractData(formData, onProgress = null, options = {}) {
    try {
      const url = options.isSingle ? '/extract-single' : '/extract';
      
      // Si se proporciona sessionId y es subida individual, agregarlo al formData
      if (options.sessionId && options.isSingle) {
        formData.append('session_id', options.sessionId);
      }
      
      // Usar timeout desde configuración centralizada
      const extractionTimeoutDefault = API_CONFIG.TIMEOUT.DEFAULT;
      const response = await apiClients.extraction.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: options.timeout || extractionTimeoutDefault,
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.lengthComputable) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress({
              type: 'upload',
              progress: percentCompleted,
              stage: 'Subiendo archivos...'
            });
          }
        },
        onDownloadProgress: (progressEvent) => {
          if (onProgress && progressEvent.lengthComputable) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            onProgress({
              type: 'processing',
              progress: percentCompleted,
              stage: 'Procesando con IA...'
            });
          }
        }
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      let errorMessage = 'Error desconocido durante la extracción';
      
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Errores de validación de FastAPI
          errorMessage = error.response.data.detail
            .map(err => `${err.loc?.join('.')}: ${err.msg}`)
            .join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage,
        statusCode: error.response?.status
      };
    }
  },

  /**
   * Exportar resultados a Excel
   * @param {Array} results - Resultados a exportar
   * @param {Array} selectedFields - Campos seleccionados para mostrar
   * @returns {Promise} Blob del archivo Excel
   */
  async exportToExcel(results, selectedFields = []) {
    try {
      const response = await apiClients.extraction.post('/export-excel', {
        data: results,
        selectedFields: selectedFields
      }, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Extraer el nombre del archivo del header Content-Disposition
      let filename = 'extraccion.xlsx'; // fallback por defecto
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, ''); // Remover comillas si las hay
        }
      }

      return {
        success: true,
        data: response.data,
        filename: filename
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Error al exportar a Excel'
      };
    }
  },

  /**
   * Obtener historial de extracciones del usuario
   * @returns {Promise} Lista de extracciones pasadas
   */
  async getExtractionHistory() {
    try {
      const response = await apiClients.extraction.get('/history');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Error al obtener historial',
        data: { total_entries: 0, entries: [] }
      };
    }
  },

  /**
   * Limpiar historial de extracciones del usuario
   * @returns {Promise} Resultado de la operación
   */
  async clearExtractionHistory() {
    try {
      const response = await apiClients.extraction.delete('/history');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Error al limpiar historial'
      };
    }
  },

  /**
   * Obtener configuración de extracción del servidor
   * @returns {Promise} Configuración de límites y formatos
   */
  async getExtractionConfig() {
    try {
      const response = await apiClients.extraction.get('/config');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || error.message || 'Error al obtener configuración',
        data: {
          max_file_size_mb: 50,
          max_file_size_zip_mb: 200,
          max_files_per_extraction: 50,
          default_max_pages_per_pdf: 1,
          supported_extensions: ["pdf", "png", "jpg", "jpeg", "zip"],
          extraction_methods: ["ocr_text", "image"],
          quota_thresholds: {
            critical: 10,
            low: 50
          }
        }
      };
    }
  },
};

export default extractionAPI;
